import { useState, useEffect } from 'react';
import { FolderOpen, Plus, Search, Filter, Copy, Trash2, Eye, BarChart3, FileDown } from 'lucide-react';
import toast from 'react-hot-toast';

const PROJECT_TYPES = [
  { id: 'fusion', label: 'Fusionner des formations', icon: '🔀', description: 'Analyser les correspondances entre plusieurs formations' },
  { id: 'gap', label: 'Trouver des compétences manquantes', icon: '🔍', description: 'Identifier les écarts de compétences entre référentiels' },
  { id: 'veille', label: 'Veille réglementaire', icon: '📋', description: 'Suivre les évolutions des référentiels RNCP' },
  { id: 'passerelle', label: 'Créer des passerelles', icon: '🌉', description: 'Définir des parcours de transition entre formations' },
  { id: 'analyse', label: 'Analyse de correspondance', icon: '📊', description: 'Comparer en détail deux référentiels' },
  { id: 'audit', label: 'Audit de compétences', icon: '✅', description: 'Évaluer la conformité d\'un référentiel' }
];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [filters, setFilters] = useState({ status: 'all', type: 'all', search: '' });

  const [newProject, setNewProject] = useState({
    name: '',
    type: '',
    description: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchProjects();
  }, [filters]);

  const fetchProjects = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.status !== 'all') queryParams.append('status', filters.status);
      if (filters.type !== 'all') queryParams.append('type', filters.type);
      if (filters.search) queryParams.append('search', filters.search);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/projects?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();
      if (data.success) {
        setProjects(data.data.projects || []);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des projets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name || !newProject.type) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newProject)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Projet créé avec succès');
        setShowCreateModal(false);
        setCurrentStep(1);
        setNewProject({ name: '', type: '', description: '', status: 'draft' });
        fetchProjects();
      } else {
        toast.error(data.message || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création du projet');
    }
  };

  const handleDuplicate = async (projectId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/projects/${projectId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Projet dupliqué avec succès');
        fetchProjects();
      }
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleExportPDF = async (projectId, projectName) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/projects/${projectId}/export/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'export');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName.replace(/[^a-z0-9]/gi, '_')}-correspondances.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('PDF téléchargé avec succès');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Projet supprimé');
        fetchProjects();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-secondary-100 text-secondary-800',
      completed: 'bg-blue-100 text-blue-800',
      archived: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.draft;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Brouillon',
      active: 'Actif',
      completed: 'Terminé',
      archived: 'Archivé'
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <FolderOpen className="w-8 h-8" />
                Gestion des projets
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Créez et gérez vos projets de correspondance pédagogique
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nouveau projet
            </button>
          </div>

          {/* Filtres */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un projet..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="draft">Brouillon</option>
              <option value="active">Actif</option>
              <option value="completed">Terminé</option>
              <option value="archived">Archivé</option>
            </select>

            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Tous les types</option>
              {PROJECT_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Liste des projets */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun projet</h3>
            <p className="text-gray-500 mb-6">Créez votre premier projet pour commencer</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Créer un projet
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div key={project.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{project.name}</h3>
                      <p className="text-sm text-gray-500 line-clamp-2">{project.description}</p>
                    </div>
                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                      {getStatusLabel(project.status)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                    <span className="text-xl">{PROJECT_TYPES.find(t => t.id === project.type)?.icon || '📁'}</span>
                    <span>{PROJECT_TYPES.find(t => t.id === project.type)?.label || project.type}</span>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-xs text-gray-500">
                      {new Date(project.createdAt).toLocaleDateString('fr-FR')}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => window.location.href = `/projects/${project.id}`}
                        className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Voir"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleExportPDF(project.id, project.name)}
                        className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Export PDF"
                      >
                        <FileDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(project.id)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Dupliquer"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de création pas à pas */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Progress bar */}
            <div className="bg-gray-100 px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Créer un nouveau projet</h3>
                <span className="text-sm text-gray-500">Étape {currentStep} / 3</span>
              </div>
              <div className="w-full bg-gray-300 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / 3) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Étape 1: Type de projet */}
              {currentStep === 1 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Quel type de projet souhaitez-vous créer ?</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {PROJECT_TYPES.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setNewProject({ ...newProject, type: type.id })}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          newProject.type === type.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-3xl mb-2">{type.icon}</div>
                        <div className="font-medium text-gray-900 mb-1">{type.label}</div>
                        <div className="text-sm text-gray-500">{type.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Étape 2: Informations du projet */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Informations du projet</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom du projet *</label>
                    <input
                      type="text"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      placeholder="Ex: Fusion BTS SIO / BUT Informatique"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      placeholder="Décrivez l'objectif de ce projet..."
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Statut initial</label>
                    <select
                      value={newProject.status}
                      onChange={(e) => setNewProject({ ...newProject, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="draft">Brouillon</option>
                      <option value="active">Actif</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Étape 3: Confirmation */}
              {currentStep === 3 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Récapitulatif</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Type de projet:</span>
                      <div className="font-medium text-gray-900 mt-1">
                        {PROJECT_TYPES.find(t => t.id === newProject.type)?.icon} {PROJECT_TYPES.find(t => t.id === newProject.type)?.label}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Nom:</span>
                      <div className="font-medium text-gray-900 mt-1">{newProject.name || '-'}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Description:</span>
                      <div className="text-gray-900 mt-1">{newProject.description || '-'}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Statut:</span>
                      <div className="mt-1">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(newProject.status)}`}>
                          {getStatusLabel(newProject.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
              <button
                onClick={() => {
                  if (currentStep === 1) {
                    setShowCreateModal(false);
                    setCurrentStep(1);
                    setNewProject({ name: '', type: '', description: '', status: 'draft' });
                  } else {
                    setCurrentStep(currentStep - 1);
                  }
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                {currentStep === 1 ? 'Annuler' : 'Précédent'}
              </button>

              <button
                onClick={() => {
                  if (currentStep === 3) {
                    handleCreateProject();
                  } else {
                    if (currentStep === 1 && !newProject.type) {
                      toast.error('Veuillez sélectionner un type de projet');
                      return;
                    }
                    setCurrentStep(currentStep + 1);
                  }
                }}
                disabled={currentStep === 1 && !newProject.type}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {currentStep === 3 ? 'Créer le projet' : 'Suivant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
